import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Recycle, FileText, PenNib } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { formatApiError } from "../../lib/api";
import { pops, popsPath, num, hasPops, WTN_STATUS } from "../../bespoke/PopsWasteList/popsApi";

/** On a saved invoice: POPs totals + the waste transfer note (auto-drafted when the invoice is issued, or created here). */
export function PopsInvoiceCard({ invoiceId, user, refreshKey, className = "" }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const enabled = hasPops(user);
  const load = useCallback(() => pops.invoiceSummary(invoiceId).then(setS).catch(() => setS(null)), [invoiceId]);
  useEffect(() => { if (enabled && invoiceId && invoiceId !== "new") load(); }, [enabled, invoiceId, load, refreshKey]);
  if (!enabled || !s || !s.pops_lines) return null;
  const w = s.transfer_note;
  const create = async () => {
    setBusy(true);
    try { await pops.createTransferNote({ invoice_id: invoiceId }); toast.success("Transfer note drafted"); load(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't draft the note"); } finally { setBusy(false); }
  };
  return (
    <div className={`bg-card border border-border rounded-lg p-4 border-l-4 border-l-emerald-600 ${className}`} data-testid="pops-invoice-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-sm font-semibold flex items-center gap-2"><Recycle size={18} className="text-emerald-600" /> POPs waste on this invoice</h2>
        <span className="text-xs text-muted-foreground" data-testid="pops-invoice-summary">{s.pops_lines} line{s.pops_lines === 1 ? "" : "s"} · {num(s.items, 0)} items · {num(s.volume_m3, 2)} m³ · ≈ {num(s.weight_kg, 0)} kg</span>
      </div>
      <div className="mt-3 flex items-center justify-between gap-3 flex-wrap rounded-md border border-border bg-muted/30 px-3 py-2">
        {w ? (
          <>
            <div className="text-sm flex items-center gap-2 flex-wrap"><FileText size={16} className="text-muted-foreground" /><span className="font-mono font-semibold" data-testid="pops-invoice-wtn-number">{w.number}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${WTN_STATUS[w.status]?.cls}`}>{WTN_STATUS[w.status]?.label}</span>
              <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><PenNib size={12} className={w.signed_by_transferor ? "text-emerald-600" : "text-muted-foreground/40"} /> transferor <PenNib size={12} className={`ml-1 ${w.signed_by_transferee ? "text-emerald-600" : "text-muted-foreground/40"}`} /> transferee</span></div>
            <Button size="sm" variant="outline" asChild data-testid="pops-invoice-wtn-open"><Link to={popsPath(`transfer-notes/${w.id}`)}>Open transfer note</Link></Button>
          </>
        ) : (
          <>
            <p className="text-xs text-muted-foreground">A waste transfer note is drafted automatically when this invoice is sent to the client — or draft it now.</p>
            <Button size="sm" variant="outline" onClick={create} disabled={busy} data-testid="pops-invoice-wtn-create">{busy ? "Drafting…" : "Draft transfer note"}</Button>
          </>
        )}
      </div>
    </div>
  );
}
